// ============================================================================
// ForcesProfilsPanel — agregats Gallup/Nova anonymes (E1b DEFERES, F-213/214/215)
// ============================================================================
// Port du PoC components/equipes/forces-profils-panel.tsx @ 7e40e4a.
// Recoit UNIQUEMENT des nombres pre-agregees (aucun nom/email) via `analysis`.
// DonutChart + GaugeBar du parc (PAS de recharts — DonutChart SVG deja en gest).
// Regle #4 : zero emoji.

import { useMemo, useState } from 'react';
import { Sparkles, Users, TrendingUp, TrendingDown, PieChart, Palette } from 'lucide-react';
import { EmptyState, GsSelect, KpiCard, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  GALLUP_STRENGTHS,
  type GallupDomain,
} from '../../lib/profile/gallup';
import {
  NOVA_BAR_CLASSES,
  NOVA_COLORS,
  type NovaColor,
} from '../../lib/profile/nova';
import {
  domainOfStrength,
  type ProfileAnalysis,
  type ScopeAnalysis,
} from '../../lib/profile/aggregate';
import { DonutChart, chartCatColor } from '../salary/DonutChart';
import { GaugeBar } from '../employee/ui';

const DOMAIN_ORDER: GallupDomain[] = [
  'executing',
  'influencing',
  'relationship',
  'strategic',
];

const DOMAIN_COLOR: Record<GallupDomain, string> = {
  executing: chartCatColor(0),
  influencing: chartCatColor(1),
  relationship: chartCatColor(2),
  strategic: chartCatColor(3),
};

const TOP_THEMES = 8;
const TOTAL_THEMES = GALLUP_STRENGTHS.length;

function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

export function ForcesProfilsPanel({ analysis }: { analysis: ProfileAnalysis }) {
  const { t } = useTranslation();
  const [selectedKey, setSelectedKey] = useState('all');

  const scope = useMemo<ScopeAnalysis>(() => {
    if (selectedKey === 'all') return analysis.global;
    return analysis.teams.find((team) => team.key === selectedKey) ?? analysis.global;
  }, [analysis, selectedKey]);

  const filterOptions = [
    { value: 'all', label: t('gest:equipes.forces.filterAll') },
    ...analysis.teams.map((team) => ({ value: team.key, label: team.label })),
  ];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-prose text-sm text-slate-500 dark:text-slate-400">
          {t('gest:equipes.forces.intro')}
          {' '}
          {scope.key === 'all'
            ? t('gest:equipes.forces.introGlobal')
            : t('gest:equipes.forces.introTeam', { team: scope.label })}
        </p>
        <GsSelect
          ariaLabel={t('gest:equipes.forces.filterAria')}
          className="w-56"
          value={selectedKey}
          onChange={setSelectedKey}
          options={filterOptions}
        />
      </div>

      {analysis.suppressedTeams > 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('gest:equipes.forces.suppressed', { n: analysis.suppressedTeams })}
        </p>
      ) : null}

      <ForcesSection scope={scope} />
      <NovaSection scope={scope} />
    </div>
  );
}

function ForcesSection({ scope }: { scope: ScopeAnalysis }) {
  const { t } = useTranslation();
  const { forces, headcount } = scope;

  const domains = DOMAIN_ORDER.map((d) => ({
    domain: d,
    label: t(`gest:equipes.forces.domains.${d}`),
    score: forces.domainScores[d],
    color: DOMAIN_COLOR[d],
  }));
  const totalScore = domains.reduce((sum, d) => sum + d.score, 0);

  const ranked = [...domains].sort((a, b) => b.score - a.score);
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];

  const topThemes = Object.entries(forces.themeScores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_THEMES);
  const maxThemeScore = topThemes.length > 0 ? topThemes[0][1] : 0;
  const missingThemes = TOTAL_THEMES - Object.keys(forces.themeScores).length;

  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {t('gest:equipes.forces.forcesTitle')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('gest:equipes.forces.forcesDescription')}
        </p>
      </header>

      {forces.respondents === 0 || totalScore === 0 ? (
        <EmptyState
          icon={<Sparkles size={48} aria-hidden="true" />}
          title={t('gest:equipes.forces.forcesEmptyTitle')}
          description={t('gest:equipes.forces.forcesEmptyDescription')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <KpiCard
              icon={TrendingUp}
              variant="brand"
              label={t('gest:equipes.forces.strongestDomain')}
              value={strongest.label}
            />
            <KpiCard
              icon={TrendingDown}
              variant="blue"
              label={t('gest:equipes.forces.weakestDomain')}
              value={weakest.label}
            />
            <KpiCard
              icon={PieChart}
              variant="green"
              label={t('gest:equipes.forces.coverage')}
              value={`${forces.respondents}/${headcount}`}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {missingThemes > 0
              ? t('gest:equipes.forces.missingThemes', { n: missingThemes, total: TOTAL_THEMES })
              : t('gest:equipes.forces.allThemes')}
            {' · '}
            {t('gest:equipes.forces.domainShare', { pct: pct(strongest.score, totalScore) })}
          </p>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className={`${GS_COMPONENTS.card} p-4`}>
              <div className="flex flex-col items-center gap-5 sm:flex-row">
                <DonutChart
                  slices={domains.map((d) => ({
                    label: d.label,
                    value: d.score,
                    color: d.color,
                  }))}
                  size={168}
                  ariaLabel={t('gest:equipes.forces.donutAria')}
                />
                <ul className="flex w-full flex-col gap-2">
                  {ranked.map((d) => (
                    <li
                      key={d.domain}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="size-3 shrink-0 rounded-full"
                          style={{ backgroundColor: d.color }}
                        />
                        <span className="truncate">{d.label}</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                        {pct(d.score, totalScore)} %
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={`${GS_COMPONENTS.card} space-y-2.5 p-4`}>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {t('gest:equipes.forces.topThemes')}
              </p>
              <ul className="flex flex-col gap-2.5">
                {topThemes.map(([name, score]) => {
                  const d = domainOfStrength(name);
                  return (
                    <li key={name} className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex min-w-0 items-center gap-2">
                          <span
                            className="size-2.5 shrink-0 rounded-full"
                            style={{
                              backgroundColor: d ? DOMAIN_COLOR[d] : 'var(--muted-foreground, #94a3b8)',
                            }}
                          />
                          <span className="truncate">{name}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                          {score}
                        </span>
                      </div>
                      <GaugeBar value={score} max={maxThemeScore} className="bg-brand-500/70" />
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function NovaSection({ scope }: { scope: ScopeAnalysis }) {
  const { t } = useTranslation();
  const { nova, headcount } = scope;

  const colors = [...NOVA_COLORS].sort(
    (a, b) => nova.averages[b] - nova.averages[a],
  );
  const dominant: NovaColor = colors[0];

  return (
    <section className="space-y-3">
      <header>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {t('gest:equipes.forces.novaTitle')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('gest:equipes.forces.novaDescription')}
        </p>
      </header>

      {nova.respondents === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:equipes.forces.novaEmptyTitle')}
          description={t('gest:equipes.forces.novaEmptyDescription')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <KpiCard
              icon={Palette}
              variant="brand"
              label={t('gest:equipes.forces.dominantColor')}
              value={t(`gest:equipes.forces.nova.${dominant}`)}
            />
            <KpiCard
              icon={Users}
              variant="green"
              label={t('gest:equipes.forces.coverage')}
              value={`${nova.respondents}/${headcount}`}
            />
          </div>

          <div className={`${GS_COMPONENTS.card} p-4`}>
            <ul className="flex flex-col gap-3">
              {colors.map((c) => (
                <li key={c} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={`size-3 shrink-0 rounded-full ${NOVA_BAR_CLASSES[c]}`}
                      />
                      <span className="font-medium">{t(`gest:equipes.forces.nova.${c}`)}</span>
                      <span className="truncate text-xs text-slate-500 dark:text-slate-400">
                        {t(`gest:equipes.forces.novaDesc.${c}`)}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                      {Math.round(nova.averages[c])} %
                    </span>
                  </div>
                  <GaugeBar
                    value={nova.averages[c]}
                    max={100}
                    className={NOVA_BAR_CLASSES[c]}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
